import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, Armchair, Circle, Pencil, Plus, Square, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { tablesService, type DiningTable, type DiningTableShape } from '@/services/tables.service';

const SHAPE_ICON = { square: Square, round: Circle, booth: Armchair } as const;
const SHAPES: Array<{ value: DiningTableShape; label: string }> = [
  { value: 'square', label: 'Square' }, { value: 'round', label: 'Round' }, { value: 'booth', label: 'Booth' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
  onChanged: () => void;
}

export function ManageTablesModal({ open, onClose, isAdmin, onChanged }: Props) {
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [area, setArea] = useState('');
  const [seats, setSeats] = useState('4');
  const [shape, setShape] = useState<DiningTableShape>('square');
  const [adding, setAdding] = useState(false);

  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [eName, setEName] = useState('');
  const [eArea, setEArea] = useState('');
  const [eSeats, setESeats] = useState('4');
  const [eShape, setEShape] = useState<DiningTableShape>('square');
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setTables((await tablesService.list(true)).items); }
    catch { /* interceptor toasts */ } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const notifyChanged = () => { onChanged(); };

  const add = async () => {
    if (!name.trim()) { toast.error('Give the table a name.'); return; }
    setAdding(true);
    try {
      await tablesService.create({ name: name.trim(), area: area.trim() || undefined, seats: parseInt(seats, 10) || 1, shape });
      setName(''); setArea(''); setSeats('4'); setShape('square');
      toast.success('Table added');
      await load(); notifyChanged();
    } catch (e: any) { if (!e?.response) toast.error('Could not add the table.'); }
    finally { setAdding(false); }
  };

  const startEdit = (t: DiningTable) => {
    setEditingUid(t.tableUid); setEName(t.name); setEArea(t.area ?? ''); setESeats(String(t.seats)); setEShape(t.shape);
  };

  const saveEdit = async () => {
    if (!editingUid) return;
    if (!eName.trim()) { toast.error('Give the table a name.'); return; }
    setSavingEdit(true);
    try {
      await tablesService.update(editingUid, { name: eName.trim(), area: eArea.trim(), seats: parseInt(eSeats, 10) || 1, shape: eShape });
      setEditingUid(null);
      toast.success('Table updated');
      await load(); notifyChanged();
    } catch (e: any) { if (!e?.response) toast.error('Could not update the table.'); }
    finally { setSavingEdit(false); }
  };

  const toggleActive = async (t: DiningTable) => {
    try { await tablesService.update(t.tableUid, { isActive: !t.isActive }); await load(); notifyChanged(); }
    catch (e: any) { if (!e?.response) toast.error('Could not update the table.'); }
  };

  const remove = async (t: DiningTable) => {
    if (!window.confirm(`Delete "${t.name}"? This can't be undone.`)) return;
    try { await tablesService.remove(t.tableUid); await load(); notifyChanged(); }
    catch (e: any) { if (!e?.response) toast.error('Could not delete the table.'); }
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...tables];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setTables(next);
    try { await tablesService.reorder(next.map((t) => t.tableUid)); notifyChanged(); }
    catch (e: any) { if (!e?.response) toast.error('Could not reorder.'); await load(); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage tables" widthClass="max-w-2xl">
      {isAdmin && (
        <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-dashed border-border p-3 sm:grid-cols-5 sm:items-end">
          <FormField label="Name" htmlFor="tbl-name"><Input id="tbl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="T1" /></FormField>
          <FormField label="Area" htmlFor="tbl-area"><Input id="tbl-area" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Indoor" /></FormField>
          <FormField label="Seats" htmlFor="tbl-seats"><Input id="tbl-seats" type="number" min={1} max={30} value={seats} onChange={(e) => setSeats(e.target.value)} /></FormField>
          <FormField label="Shape" htmlFor="tbl-shape">
            <Select id="tbl-shape" value={shape} onChange={(e) => setShape(e.target.value as DiningTableShape)}>
              {SHAPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </FormField>
          <Button size="md" iconLeft={<Plus size={15} />} loading={adding} onClick={add} className="mb-4">Add table</Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-12 rounded-lg" />)}</div>
      ) : tables.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-muted">No tables yet — add your first one above.</p>
      ) : (
        <ul className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
          {tables.map((t, idx) => {
            const ShapeIcon = SHAPE_ICON[t.shape];
            const editing = editingUid === t.tableUid;
            return (
              <li key={t.tableUid} className={clsx('rounded-xl border px-3 py-2.5', t.isActive ? 'border-border bg-surface' : 'border-dashed border-border bg-surface-alt opacity-70')}>
                {editing ? (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:items-end">
                    <FormField label="Name"><Input value={eName} onChange={(e) => setEName(e.target.value)} /></FormField>
                    <FormField label="Area"><Input value={eArea} onChange={(e) => setEArea(e.target.value)} /></FormField>
                    <FormField label="Seats"><Input type="number" min={1} max={30} value={eSeats} onChange={(e) => setESeats(e.target.value)} /></FormField>
                    <FormField label="Shape">
                      <Select value={eShape} onChange={(e) => setEShape(e.target.value as DiningTableShape)}>
                        {SHAPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </Select>
                    </FormField>
                    <div className="col-span-2 flex justify-end gap-2 sm:col-span-4">
                      <Button size="sm" variant="secondary" onClick={() => setEditingUid(null)}>Cancel</Button>
                      <Button size="sm" loading={savingEdit} onClick={saveEdit}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                      <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="text-text-muted hover:text-text-primary disabled:opacity-30"><ArrowUp size={13} /></button>
                      <button type="button" disabled={idx === tables.length - 1} onClick={() => move(idx, 1)} className="text-text-muted hover:text-text-primary disabled:opacity-30"><ArrowDown size={13} /></button>
                    </div>
                    <ShapeIcon size={16} className="shrink-0 text-text-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-text-primary">{t.name}</div>
                      <div className="truncate text-2xs text-text-muted">{t.seats} seats{t.area ? ` · ${t.area}` : ''}{!t.isActive ? ' · Inactive' : ''}</div>
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(t)} aria-label="Edit"><Pencil size={14} /></Button>
                        <Button size="sm" variant={t.isActive ? 'secondary' : 'soft'} onClick={() => toggleActive(t)}>{t.isActive ? 'Deactivate' : 'Activate'}</Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(t)} aria-label="Delete"><Trash2 size={14} className="text-danger" /></Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}
